import aa from 'search-insights';

// Format price with currency
const formatPrice = (price) => {
  return new Intl.NumberFormat('ko-KR', {
    style: 'currency',
    currency: 'KRW',
  }).format(price);
};

// Get first available image or placeholder
const getProductImage = (hit) => {
  if (!hit) return 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';
  if (hit.image) return hit.image;
  if (hit.images && hit.images.length > 0) return hit.images[0];
  if (hit.images_info && hit.images_info.length > 0) return hit.images_info[0].url;
  return 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';
};

// Check if product has discount
const hasDiscount = (hit) => {
  if (!hit || !hit.compare_at_price || !hit.price) return false;
  return hit.compare_at_price > hit.price;
};

// Calculate discount percentage
const getDiscountPercent = (hit) => {
  if (!hasDiscount(hit)) return 0;
  return Math.round(((hit.compare_at_price - hit.price) / hit.compare_at_price) * 100);
};

// Render price section (returns HTML string)
const renderPriceSection = (hit, discount, discountPercent) => {
  if (!hit || !hit.price) return '';
  const price = formatPrice(hit.price);
  const comparePrice = hit.compare_at_price ? formatPrice(hit.compare_at_price) : '';

  if (discount) {
    return `
      <div class="price__container">
        <div class="price__regular">
          <span class="price-item price-item--regular">${price}</span>
        </div>
        <div class="price__sale">
          <span class="price-item price-item--sale price-item--last">
            <s class="price-item__original">${comparePrice}</s>
          </span>
          <span class="badge price__badge-sale color-accent-2">${discountPercent}% 할인</span>
        </div>
      </div>
    `;
  }

  return `
    <div class="price__regular">
      <span class="price-item price-item--regular">${price}</span>
    </div>
  `;
};

// Fetch product card HTML from Shopify
// view=card 방식을 사용하여 CSS가 포함된 전체 템플릿을 가져옴
const fetchProductCard = async (handle) => {
  try {
    if (!handle) return null;

    const locale = window.Shopify?.locale || '';
    const root = window.Shopify?.routes?.root || '/';

    // view=card 방식 사용 (CSS가 포함된 전체 템플릿)
    // root가 이미 locale을 포함하고 있으므로 바로 사용
    let url = `${root}products/${handle}/?view=card`;

    const response = await fetch(url);

    if (response.status === 200) {
      return await response.text();
    }

    return null;
  } catch (error) {
    console.error(`Failed to fetch product card for ${handle}:`, error);
    return null;
  }
};

// Initialize custom Algolia InstantSearch (완전히 새로운 검색 UI)
// .ais-page 스타일 충돌을 피하기 위해 직접 InstantSearch 초기화
export const initCustomAlgoliaSearch = () => {
  // Algolia가 완전히 초기화된 후 실행
  document.addEventListener('algolia.hooks.initialize', async function () {
    console.log('Algolia hooks initialized - starting custom InstantSearch');

    const config = window.algoliaShopify?.config;

    if (!config) {
      console.error('Algolia config not available');
      return;
    }

    // Algolia config 키 이름 확인
    const appId = config.app_id;
    const apiKey = config.search_api_key || config.api_key; // search_api_key 우선 사용

    // 로케일에 따라 인덱스 선택
    const locale = window.Shopify?.locale || '';
    let indexName = config.index_name || `${config.index_prefix}products`; // 기본 products index

    // 영어 로케일이면 __en 인덱스 사용
    if (locale === 'en') {
      indexName = `${config.index_prefix}products__en`;
    }

    if (!appId || !apiKey || !indexName) {
      console.error('Algolia config incomplete:', { appId, apiKey: !!apiKey, indexName });
      return;
    }

    // URL에서 query 파라미터 가져오기
    const urlParams = new URLSearchParams(window.location.search);
    const initialQuery = urlParams.get('query') || urlParams.get('q') || '';

    // 페이지 제목 업데이트
    const titleElement = document.getElementById('algolia-search-title');
    if (titleElement) {
      if (initialQuery) {
        titleElement.textContent = `Search Results for '${initialQuery}'`;
      } else {
        titleElement.textContent = 'All Products';
      }
    }

    console.log('Algolia config loaded:', {
      app_id: appId,
      index_name: indexName,
      locale: locale,
      has_api_key: !!apiKey,
      initial_query: initialQuery,
    });

    try {
      // InstantSearch.js 라이브러리 동적 로드
      const { default: instantsearch } = await import('instantsearch.js/es');
      const { searchBox, hits, stats, pagination, configure, refinementList, rangeInput, clearRefinements, sortBy } =
        await import('instantsearch.js/es/widgets');
      const { connectRange } = await import('instantsearch.js/es/connectors');
      const { liteClient } = await import('algoliasearch/lite');

      // Algolia search client 생성 (v5 API)
      const searchClient = liteClient(appId, apiKey);

      // Algolia Insights 초기화 (CTR/CVR 트래킹용)
      aa('init', { appId, apiKey, useCookie: true, cookieDuration: 15552000 });

      // InstantSearch 인스턴스 생성
      const search = instantsearch({
        indexName,
        searchClient,
        insights: true,
        routing: {
          stateMapping: {
            stateToRoute(uiState) {
              const indexUiState = uiState[indexName] || {};
              // query는 항상 포함 (초기값이나 현재값)
              return {
                query: indexUiState.query || initialQuery || undefined,
                page: indexUiState.page,
                sortBy: indexUiState.sortBy,
                refinementList: indexUiState.refinementList,
                range: indexUiState.range,
              };
            },
            routeToState(routeState) {
              return {
                [indexName]: {
                  // query는 URL에서 가져오거나 초기값 사용
                  query: routeState.query || initialQuery || '',
                  page: routeState.page,
                  sortBy: routeState.sortBy,
                  refinementList: routeState.refinementList,
                  range: routeState.range,
                },
              };
            },
          },
        },
        initialUiState: {
          [indexName]: {
            query: initialQuery,
          },
        },
      });

      // Widgets 추가
      search.addWidgets([
        configure({
          hitsPerPage: 24,
          query: initialQuery, // 초기 검색어 설정
          clickAnalytics: true, // queryID 반환 활성화 (CTR 트래킹용)
          // Relevance 정렬에서 품절 상품을 뒤로 (서버 측 정렬)
          //   optionalFilters: ['inventory_available:true<score=100>'],
        }),

        // 검색 통계
        stats({
          container: '#algolia-stats',
          templates: {
            text(data, { html }) {
              const { query } = data;
              let count = '';

              if (data.hasManyResults) {
                count = `${data.nbHits} products`;
              } else if (data.hasOneResult) {
                count = '1 product';
              } else {
                count = 'No results';
              }

              if (query) {
                return html`<span>${count} found for <strong>'${query}'</strong></span>`;
              } else {
                return html`<span>${count}</span>`;
              }
            },
          },
        }),

        // 정렬 기능 - Desktop
        sortBy({
          container: '#algolia-sort-by',
          items: [
            { label: 'Relevance', value: indexName },
            { label: 'Price: Low to High', value: `${indexName}_price_asc` },
            { label: 'Price: High to Low', value: `${indexName}_price_desc` },
            { label: 'Name: A-Z', value: `${indexName}_title_asc` },
            { label: 'Name: Z-A', value: `${indexName}_title_desc` },
          ],
        }),

        // 정렬 기능 - Mobile
        sortBy({
          container: '#algolia-sort-by-mobile',
          items: [
            { label: 'Relevance', value: indexName },
            { label: 'Price: Low to High', value: `${indexName}_price_asc` },
            { label: 'Price: High to Low', value: `${indexName}_price_desc` },
            { label: 'Name: A-Z', value: `${indexName}_title_asc` },
            { label: 'Name: Z-A', value: `${indexName}_title_desc` },
          ],
          cssClasses: {
            select: 'algolia-sort-mobile',
          },
        }),

        // Filters - Desktop
        // Category Filter (product_type)
        refinementList({
          container: '#algolia-filter-category',
          attribute: 'product_type',
          limit: 10,
          showMore: true,
          showMoreLimit: 20,
          templates: {
            header: '카테고리',
          },
        }),

        // Vendor Filter
        refinementList({
          container: '#algolia-filter-vendor',
          attribute: 'vendor',
          limit: 10,
          showMore: true,
          showMoreLimit: 20,
          templates: {
            header: 'Brand',
          },
        }),

        // Price Range Filter (Custom with $ prefix and auto-apply)
        connectRange((renderOptions, isFirstRender) => {
          const { start, range, refine, widgetParams } = renderOptions;
          const { container } = widgetParams;

          if (isFirstRender) {
            const containerElement = document.querySelector(container);
            containerElement.innerHTML = `
              <div class="ais-RangeInput">
                <div class="ais-Panel-header">Price Range</div>
                <div class="ais-RangeInput-form">
                  <div class="ais-RangeInput-input-wrapper">
                    <span class="ais-RangeInput-prefix">$</span>
                    <input 
                      type="number" 
                      class="ais-RangeInput-input ais-RangeInput-input--min" 
                      placeholder="Min"
                      min="${range.min || 0}"
                      max="${range.max || 0}"
                      step="10"
                    />
                  </div>
                  <span class="ais-RangeInput-separator">-</span>
                  <div class="ais-RangeInput-input-wrapper">
                    <span class="ais-RangeInput-prefix">$</span>
                    <input 
                      type="number" 
                      class="ais-RangeInput-input ais-RangeInput-input--max" 
                      placeholder="Max"
                      min="${range.min || 0}"
                      max="${range.max || 0}"
                      step="10"
                    />
                  </div>
                </div>
              </div>
            `;

            // Auto-apply on input change
            const minInput = containerElement.querySelector('.ais-RangeInput-input--min');
            const maxInput = containerElement.querySelector('.ais-RangeInput-input--max');

            const applyRange = () => {
              const min = minInput.value ? parseFloat(minInput.value) : undefined;
              const max = maxInput.value ? parseFloat(maxInput.value) : undefined;
              refine([min, max]);
            };

            // Debounce 적용 (500ms 후 자동 적용)
            let timeout;
            const debouncedApply = () => {
              clearTimeout(timeout);
              timeout = setTimeout(applyRange, 500);
            };

            minInput.addEventListener('input', debouncedApply);
            maxInput.addEventListener('input', debouncedApply);
          }

          // Update input values when refinement changes
          const containerElement = document.querySelector(container);
          const minInput = containerElement.querySelector('.ais-RangeInput-input--min');
          const maxInput = containerElement.querySelector('.ais-RangeInput-input--max');

          if (minInput && maxInput) {
            minInput.value = start[0] !== -Infinity ? start[0] : '';
            maxInput.value = start[1] !== Infinity ? start[1] : '';

            // Update min/max attributes
            if (range.min !== undefined) minInput.setAttribute('min', range.min);
            if (range.max !== undefined) {
              minInput.setAttribute('max', range.max);
              maxInput.setAttribute('min', range.min);
              maxInput.setAttribute('max', range.max);
            }
          }
        })({
          container: '#algolia-filter-price',
          attribute: 'price',
        }),

        // Clear Filters Button - Top (상단 결과 영역)
        clearRefinements({
          container: '#algolia-clear-filters-top',
          excludedAttributes: ['query'], // query는 초기화하지 않음
          templates: {
            resetLabel: 'Clear Filters',
          },
          cssClasses: {
            button: 'algolia-clear-filters-top-button',
          },
        }),

        // Clear Filters Button - Sidebar (사이드바)
        clearRefinements({
          container: '#algolia-clear-filters',
          excludedAttributes: ['query'], // query는 초기화하지 않음
          templates: {
            resetLabel: 'Clear Filters',
          },
        }),

        // Mobile Filters (동일한 필터를 모바일용 컨테이너에도 추가)
        refinementList({
          container: '#algolia-filter-category-mobile',
          attribute: 'product_type',
          limit: 10,
          showMore: true,
          templates: {
            header: '카테고리',
          },
        }),

        refinementList({
          container: '#algolia-filter-vendor-mobile',
          attribute: 'vendor',
          limit: 10,
          showMore: true,
          templates: {
            header: 'Brand',
          },
        }),

        // Price Range Filter Mobile (Custom with $ prefix and auto-apply)
        connectRange((renderOptions, isFirstRender) => {
          const { start, range, refine, widgetParams } = renderOptions;
          const { container } = widgetParams;

          if (isFirstRender) {
            const containerElement = document.querySelector(container);
            containerElement.innerHTML = `
              <div class="ais-RangeInput">
                <div class="ais-Panel-header">Price Range</div>
                <div class="ais-RangeInput-form">
                  <div class="ais-RangeInput-input-wrapper">
                    <span class="ais-RangeInput-prefix">$</span>
                    <input 
                      type="number" 
                      class="ais-RangeInput-input ais-RangeInput-input--min" 
                      placeholder="Min"
                      min="${range.min || 0}"
                      max="${range.max || 0}"
                      step="10"
                    />
                  </div>
                  <span class="ais-RangeInput-separator">-</span>
                  <div class="ais-RangeInput-input-wrapper">
                    <span class="ais-RangeInput-prefix">$</span>
                    <input 
                      type="number" 
                      class="ais-RangeInput-input ais-RangeInput-input--max" 
                      placeholder="Max"
                      min="${range.min || 0}"
                      max="${range.max || 0}"
                      step="10"
                    />
                  </div>
                </div>
              </div>
            `;

            // Auto-apply on input change
            const minInput = containerElement.querySelector('.ais-RangeInput-input--min');
            const maxInput = containerElement.querySelector('.ais-RangeInput-input--max');

            const applyRange = () => {
              const min = minInput.value ? parseFloat(minInput.value) : undefined;
              const max = maxInput.value ? parseFloat(maxInput.value) : undefined;
              refine([min, max]);
            };

            // Debounce 적용 (500ms 후 자동 적용)
            let timeout;
            const debouncedApply = () => {
              clearTimeout(timeout);
              timeout = setTimeout(applyRange, 500);
            };

            minInput.addEventListener('input', debouncedApply);
            maxInput.addEventListener('input', debouncedApply);
          }

          // Update input values when refinement changes
          const containerElement = document.querySelector(container);
          const minInput = containerElement.querySelector('.ais-RangeInput-input--min');
          const maxInput = containerElement.querySelector('.ais-RangeInput-input--max');

          if (minInput && maxInput) {
            minInput.value = start[0] !== -Infinity ? start[0] : '';
            maxInput.value = start[1] !== Infinity ? start[1] : '';

            // Update min/max attributes
            if (range.min !== undefined) minInput.setAttribute('min', range.min);
            if (range.max !== undefined) {
              minInput.setAttribute('max', range.max);
              maxInput.setAttribute('min', range.min);
              maxInput.setAttribute('max', range.max);
            }
          }
        })({
          container: '#algolia-filter-price-mobile',
          attribute: 'price',
        }),

        clearRefinements({
          container: '#algolia-clear-filters-mobile',
          excludedAttributes: ['query'], // query는 초기화하지 않음
          templates: {
            resetLabel: 'Clear Filters',
          },
        }),

        // 검색 결과
        hits({
          container: '#algolia-hits',
          templates: {
            item(hit, { html, components, sendEvent }) {
              // 각 hit에 대해 product-card를 동적으로 가져옴
              const handle = hit.handle;
              const cardId = `algolia-product-${handle}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

              // 비동기로 product card 가져오기
              fetchProductCard(handle).then((cardHtml) => {
                if (cardHtml) {
                  const cardElement = document.getElementById(cardId);
                  if (cardElement) {
                    cardElement.innerHTML = cardHtml;

                    // 클릭 이벤트 바인딩 (CTR 트래킹)
                    cardElement.addEventListener('click', () => {
                      sendEvent('click', hit, 'Product Clicked');
                      window.amplitude?.track('Search Result Clicked', {
                        query: search.helper.state.query || '',
                        product_handle: handle,
                        source: 'search_page',
                        position: hit.__position,
                      });
                    });
                  }
                }
              });
              // 로딩 상태 반환 (skeleton)
              return `
            <div id="${cardId}" class="algolia-product-card-wrapper">
              <div class="algolia-product-loading" style="border-radius: 8px; overflow: hidden; background: white;">
                <div style="width: 100%; padding-top: 100%; position: relative; background: #f3f4f6;"></div>
                <div style="padding: 12px;">
                  <div style="height: 16px; background: #e5e7eb; border-radius: 4px; margin-bottom: 8px;"></div>
                  <div style="height: 14px; background: #e5e7eb; border-radius: 4px; margin-bottom: 8px; width: 60%;"></div>
                  <div style="height: 20px; background: #e5e7eb; border-radius: 4px; width: 40%;"></div>
                </div>
              </div>
            </div>
          `;
            },
          },
        }),

        // 페이지네이션
        pagination({
          container: '#algolia-pagination',
          padding: 2,
          showFirst: false,
          showLast: false,
          templates: {
            previous: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>`,
            next: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>`,
          },
        }),
      ]);

      // InstantSearch 시작
      search.start();

      // Amplitude: 검색 결과 이벤트
      let lastTrackedQuery = '';
      search.on('render', () => {
        const query = search.helper.state.query || '';
        if (query && query !== lastTrackedQuery) {
          lastTrackedQuery = query;
          const nbHits = search.helper.lastResults?.nbHits ?? 0;
          window.amplitude?.track('Search Submitted', { query, results_count: nbHits, source: 'search_page' });
          if (nbHits === 0) {
            window.amplitude?.track('Search No Results', { query, source: 'search_page' });
          }
        }
      });

      // sortBy 변경 감지하여 optionalFilters 동적 조정 (한 번만 실행)
      let previousIndex = indexName;
      let isUpdating = false;

      search.on('render', () => {
        if (isUpdating) return; // 업데이트 중이면 스킵

        const state = search.helper.state;
        const currentIndex = state.index || indexName;

        // 인덱스가 실제로 변경되었을 때만 처리
        if (currentIndex !== previousIndex) {
          previousIndex = currentIndex;
          isUpdating = true;

          const isRelevanceSort = currentIndex === indexName;
          const currentOptionalFilters = state.optionalFilters || [];
          const shouldHaveFilter = isRelevanceSort;
          const hasFilter = currentOptionalFilters.length > 0;

          // 필터 상태가 달라야 할 때만 업데이트
          if (shouldHaveFilter && !hasFilter) {
            search.helper.setQueryParameter('optionalFilters', ['inventory_available:true<score=100>']);
            search.helper.search();
          } else if (!shouldHaveFilter && hasFilter) {
            search.helper.setQueryParameter('optionalFilters', []);
            search.helper.search();
          }

          // 다음 렌더링 후 업데이트 플래그 해제
          setTimeout(() => {
            isUpdating = false;
          }, 100);
        }
      });

      console.log('Custom Algolia InstantSearch started');
    } catch (error) {
      console.error('Failed to initialize custom InstantSearch:', error);
    }
  });
};

// Window에 함수 노출
if (typeof window !== 'undefined') {
  window.initCustomAlgoliaSearch = initCustomAlgoliaSearch;
}

// 기존 Shopify widget용 hook은 제거하고 Custom InstantSearch만 사용
// Autocomplete는 Algolia 기본 UI를 그대로 사용
export const initAlgoliaShopify = () => {
  // 더 이상 hook 기반 커스터마이징 없음
  // Custom InstantSearch로 완전히 대체
  console.log('Algolia Shopify hooks - using custom InstantSearch instead');
};
